/**
 * The one place that says what a field's options are, whatever their source: the list the server
 * serves with the descriptor (static `options`, a step's final list, `keep_offered_only`) or the list
 * fetched for it (`useDynamicOptions`: `dynamicOptions` / `loadingOptions` / `failedOptions`).
 *
 * A served list wins: it is there, never loading nor failed, and final. A fetched list is final only once
 * loaded with success. Every reader goes through it: the rendering of the field, its loading / error
 * line, the drop of a selection no longer offered, the labels of the rows that follow a field
 * (`rows_from`) and the option attributes the conditions read (`option_attribute`). Generic: no field
 * name.
 */
import type { DynamicOption } from "@/hooks/use-dynamic-options";
import type { FieldDataType } from "@/types/dynamic-detail";
import { hasStaticOptions } from "@/utils/static-options";

/**
 * Flatten a field list into the leaves that may need options, each paired with its
 * descriptor path (`routes.wrapup_names`).
 *
 * The path is the descriptor's, not the form's: every row of a repeatable fieldset
 * shares the same descriptor, so one fetch serves them all and adding a row costs
 * nothing. A field at the root keeps its plain name, so nothing changes for the forms
 * that have no fieldset.
 */
export function flattenOptionFields(fields: FieldDataType[], prefix = ""): { field: FieldDataType; key: string }[] {
    return fields.flatMap(field => {
        const key = prefix ? `${prefix}.${field.field_name}` : field.field_name;
        if (field.field_type === "fieldset") {
            return flattenOptionFields(field.subfields ?? [], key);
        }
        return [{ field, key }];
    });
}

export type FieldOptionSources = {
    dynamicOptions: Record<string, DynamicOption[]>;
    loadingOptions?: Record<string, boolean>;
    failedOptions?: Record<string, boolean>;
};

export type ResolvedFieldOptions = {
    /** The options offered now. */
    options: DynamicOption[];
    /** Attributes per option value: `option_metadata` of the descriptor, then each option's `metadata`. */
    metadata: Record<string, Record<string, unknown>>;
    /** The field's own list is being fetched (a served list never is). */
    loading: boolean;
    /** The field's own list failed to load (a served list never did). */
    failed: boolean;
    /** The list is known: served, or loaded with success. Only then may a selection be judged against it. */
    final: boolean;
};

function servedOptions(field: FieldDataType): DynamicOption[] {
    return (field.options ?? []).map(option => (typeof option === "string" ? { value: option, label: option } : (option as DynamicOption)));
}

/** A field's effective options (`path`: its descriptor path, its name at the root). */
export function resolveFieldOptions(field: FieldDataType, path: string, sources: FieldOptionSources): ResolvedFieldOptions {
    const served = hasStaticOptions(field);
    const fetched = sources.dynamicOptions[path];
    const options = served ? servedOptions(field) : (fetched ?? []);
    const metadata: Record<string, Record<string, unknown>> = {};
    options.forEach(option => {
        if (option.value != null && option.metadata) metadata[String(option.value)] = { ...option.metadata };
    });
    Object.entries(field.option_metadata ?? {}).forEach(([value, attributes]) => {
        metadata[value] = { ...(metadata[value] ?? {}), ...(attributes as Record<string, unknown>) };
    });
    return {
        options,
        metadata,
        loading: !served && Boolean(sources.loadingOptions?.[path]),
        failed: !served && Boolean(sources.failedOptions?.[path]),
        final: served || fetched !== undefined
    };
}

/**
 * The options of every field of `fields` by descriptor path, served lists winning over fetched ones; a field
 * whose list is not known is left out. What a container hands its fields and its conditions.
 */
export function effectiveOptionMap(fields: FieldDataType[], sources: FieldOptionSources): Record<string, DynamicOption[]> {
    const map: Record<string, DynamicOption[]> = { ...sources.dynamicOptions };
    flattenOptionFields(fields).forEach(({ field, key }) => {
        if (hasStaticOptions(field)) map[key] = servedOptions(field);
    });
    return map;
}

/**
 * The descriptors a form shows now: the form's own, each replaced by the one the current step serves
 * (its options, `option_metadata` and other overrides) when the step serves it.
 */
export function currentDescriptors(fields: FieldDataType[], served: FieldDataType[] | undefined): FieldDataType[] {
    if (!served?.length) return fields;
    const byName = new Map(served.map(field => [field.field_name, field]));
    const known = new Set(fields.map(field => field.field_name));
    return [...fields.map(field => byName.get(field.field_name) ?? field), ...served.filter(field => !known.has(field.field_name))];
}
